import React, { useState, useCallback, memo, useEffect, useRef, useMemo } from 'react';
import { useDeepgramVoiceAgent } from '../../hooks/useDeepgramVoiceAgent';
import { voiceAssistantEventBus, VOICE_EVENTS } from '../../utils/voiceAssistantEventBus.js';
import { mascotEventBus, MASCOT_EVENTS } from '../../utils/mascotEventBus.js'; // Phase 6
import { useAuthStore } from '../../stores/authStore';
import { useLocation, useNavigate } from 'react-router-dom';
import './FloatingVoiceAssistant.css';
import '../../styles/mascot-animations.css'; // Phase 6: Import mascot animations

/**
 * FloatingVoiceAssistant - Universal Draggable Voice Assistant
 * 
 * Phase 1: Always visible + draggable (like Dino mascot)
 * - Visible on ALL pages (not just homework/exercise sessions)
 * - Fully draggable with position persistence
 * - Gray/muted when idle (waiting for activation)
 * - Connection only when clicked or wake word detected
 * 
 * Future phases will add:
 * - Click-to-activate Deepgram connection
 * - Wake word detection ("Hey Dino")
 * - General platform help capabilities
 * - Mascot animations (celebrate, encourage, etc.)
 * 
 * @param {string} sessionId - Current session ID (homework sessionId or exercise attemptId) - can be null
 * @param {string} sessionType - Type of session ('homework' or 'exercise') 
 * @param {string} pageContext - Current page context ('workspace', 'results', 'voice-tutor', etc.)
 * @param {boolean} shouldConnect - Whether to connect to voice agent (default: false until Phase 3)
 */
const deriveCategoryFromPath = (pathname = '') => {
  if (pathname.startsWith('/exercises')) return 'exercise';
  if (pathname.startsWith('/homework')) return 'homework';
  if (pathname.startsWith('/learn')) return 'learn';
  if (pathname.startsWith('/profile')) return 'profile';
  return 'general';
};

const FloatingVoiceAssistantCore = ({ sessionId, sessionType = 'homework', pageContext, shouldConnect = false }) => {
  // UI State
  const [isExpanded, setIsExpanded] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  
  // Dragging State
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const currentPositionRef = useRef({ x: 0, y: 0 });
  const positionLoadedRef = useRef(false);
  const iconRef = useRef(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Phase 6: Animation State (migrated from FloatingIcon)
  const [animation, setAnimation] = useState(null);
  const [animationClass, setAnimationClass] = useState('');
  const animationTimeoutRef = useRef(null);
  const lastSuccessAnimationRef = useRef(null);
  const lastFailureAnimationRef = useRef(null);

  const isVoiceTutorPage = pageContext === 'voice-tutor' || pageContext === 'exercise-voice-tutor';

  // Load saved position on mount (only once)
  useEffect(() => {
    if (positionLoadedRef.current) return;
    
    console.log('🎤 FloatingVoiceAssistant mounted - Phase 1 (always visible + draggable)');
    const savedPosition = localStorage.getItem('floating-voice-position');
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        console.log('📍 Loaded saved voice icon position - x:', pos.x, 'y:', pos.y);
        
        // Check if position is off-screen and reset if needed
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        if (Math.abs(pos.x) > windowWidth || Math.abs(pos.y) > windowHeight) {
          console.log('⚠️ Position off-screen, resetting to default');
          localStorage.removeItem('floating-voice-position');
          setPosition({ x: 0, y: 0 });
          currentPositionRef.current = { x: 0, y: 0 };
        } else {
          setPosition(pos);
          currentPositionRef.current = pos;
        }
      } catch (e) {
        console.error('Failed to parse saved position:', e);
        localStorage.removeItem('floating-voice-position');
      }
    } else {
      console.log('📍 No saved position, using default (0, 0)');
    }
    
    positionLoadedRef.current = true;
  }, []);

  // Sync position ref with state
  useEffect(() => {
    currentPositionRef.current = position;
  }, [position]);

  // Stabilize callbacks to prevent unnecessary re-renders and reconnections
  const handleTranscript = useCallback((full, partial) => {
    setTranscript(full);
    setPartialTranscript(partial);
  }, []);

  // Phase 3: Determine connection type and whether to connect
  // - On homework/exercise pages with sessionId: connect to their specific agent
  // - On other pages: connect to general help agent (using userId)
  // - Don't auto-connect - only connect when activated (click or wake word)
  const [isActivated, setIsActivated] = useState(false);
  
  // Get userId and preferences from auth store
  const user = useAuthStore((state) => state.user);
  const userId = user?.user_id || user?.id || null;

  // Determine which agent to use
  const agentType = sessionId ? sessionType : 'general';
  const agentSessionId = sessionId || userId;
  const inferredCategory = sessionId ? sessionType : deriveCategoryFromPath(location.pathname);

  const pageContextPayload = useMemo(() => {
    return {
      location: {
        route: location.pathname,
        category: inferredCategory,
        pageContext: pageContext || 'general'
      },
      session: {
        homeworkSessionId: inferredCategory === 'homework' ? sessionId : null,
        exerciseAttemptId: inferredCategory === 'exercise' ? sessionId : null
      },
      capabilities: {
        canOpenDinoHelp: inferredCategory === 'exercise' || pageContext === 'exercise-voice-tutor',
        canUploadHomework: true
      }
    };
  }, [location.pathname, inferredCategory, pageContext, sessionId]);

  // Phase 6: Animation trigger function (migrated from FloatingIcon)
  const triggerAnimation = useCallback((animType) => {
    // Don't animate while dragging
    if (isDragging) return;

    // Clear any existing animation timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // Map animation types to classes
    const animationMap = {
      celebrate: 'mascot-animate-celebrate',
      encourage: 'mascot-animate-encourage',
      victory: 'mascot-animate-victory',
      support: 'mascot-animate-support',
      'success-1': 'mascot-animate-success-1',
      'success-2': 'mascot-animate-success-2',
      'success-3': 'mascot-animate-success-3',
      'success-4': 'mascot-animate-success-4',
      'success-5': 'mascot-animate-success-5',
      'success-6': 'mascot-animate-success-6',
      'success-7': 'mascot-animate-success-7',
      'success-8': 'mascot-animate-success-8',
      'failure-1': 'mascot-animate-failure-1',
      'failure-2': 'mascot-animate-failure-2',
      'failure-3': 'mascot-animate-failure-3',
      'failure-4': 'mascot-animate-failure-4',
      'failure-5': 'mascot-animate-failure-5',
      'failure-6': 'mascot-animate-failure-6',
      streak: 'mascot-animate-streak',
      partial: 'mascot-animate-partial',
      perfect: 'mascot-animate-perfect',
      'score-excellent': 'mascot-animate-score-excellent',
      'score-good': 'mascot-animate-score-good'
    };

    setAnimation(animType);
    setAnimationClass(animationMap[animType] || '');

    // Clear animation after duration
    const durations = {
      celebrate: 1000,
      encourage: 1500,
      victory: 2000,
      support: 700,
      'success-1': 2500, 'success-2': 2500, 'success-3': 2500, 'success-4': 2500,
      'success-5': 2500, 'success-6': 2500, 'success-7': 2500, 'success-8': 2500,
      'failure-1': 2000, 'failure-2': 2000, 'failure-3': 2000,
      'failure-4': 2000, 'failure-5': 2000, 'failure-6': 2000,
      streak: 1500,
      partial: 1000,
      perfect: 2000,
      'score-excellent': 1500,
      'score-good': 1200
    };
    
    animationTimeoutRef.current = setTimeout(() => {
      setAnimation(null);
      setAnimationClass('');
      animationTimeoutRef.current = null;
    }, durations[animType] || 1000);
  }, [isDragging]);

  // Phase 6: Listen to mascot events for animations
  useEffect(() => {
    const handleExerciseCorrect = () => {
      const successAnimations = ['success-1', 'success-2', 'success-3', 'success-4', 'success-5', 'success-6', 'success-7', 'success-8'];
      const availableAnimations = successAnimations.filter(anim => anim !== lastSuccessAnimationRef.current);
      const pool = availableAnimations.length > 0 ? availableAnimations : successAnimations;
      const randomSuccess = pool[Math.floor(Math.random() * pool.length)];
      lastSuccessAnimationRef.current = randomSuccess;
      triggerAnimation(randomSuccess);
    };

    const handleExerciseIncorrect = () => {
      const failureAnimations = ['failure-1', 'failure-2', 'failure-3', 'failure-4', 'failure-5', 'failure-6'];
      const availableAnimations = failureAnimations.filter(anim => anim !== lastFailureAnimationRef.current);
      const pool = availableAnimations.length > 0 ? availableAnimations : failureAnimations;
      const randomFailure = pool[Math.floor(Math.random() * pool.length)];
      lastFailureAnimationRef.current = randomFailure;
      triggerAnimation(randomFailure);
    };

    const handleExerciseSummary = () => triggerAnimation('victory');
    const handleExerciseStreak = () => triggerAnimation('streak');
    const handleExercisePartial = () => triggerAnimation('partial');
    const handleExercisePerfect = () => triggerAnimation('perfect');
    const handleScoreExcellent = () => triggerAnimation('score-excellent');
    const handleScoreGood = () => triggerAnimation('score-good');

    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_CORRECT, handleExerciseCorrect);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_INCORRECT, handleExerciseIncorrect);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_SUMMARY, handleExerciseSummary);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_STREAK, handleExerciseStreak);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_PARTIAL, handleExercisePartial);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_PERFECT, handleExercisePerfect);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_SCORE_EXCELLENT, handleScoreExcellent);
    mascotEventBus.on(MASCOT_EVENTS.EXERCISE_SCORE_GOOD, handleScoreGood);

    return () => {
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_CORRECT, handleExerciseCorrect);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_INCORRECT, handleExerciseIncorrect);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_SUMMARY, handleExerciseSummary);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_STREAK, handleExerciseStreak);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_PARTIAL, handleExercisePartial);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_PERFECT, handleExercisePerfect);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_SCORE_EXCELLENT, handleScoreExcellent);
      mascotEventBus.off(MASCOT_EVENTS.EXERCISE_SCORE_GOOD, handleScoreGood);
    };
  }, [triggerAnimation]);

  // Cleanup animation timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const {
    isConnected,
    startListening,
    stopListening,
    isListening,
    isAgentThinking,
    isAgentSpeaking,
    error: agentError,
    reconnect,
    connect,
    disconnect,
    sendOcrSummary,
    sendValidationStatus
  } = useDeepgramVoiceAgent({
    sessionId: agentSessionId,
    type: agentType, // 'homework', 'exercise', or 'general'
    problemId: null, // General help, not problem-specific
    pageContextData: pageContextPayload,
    onTranscript: handleTranscript,
    shouldConnect: isActivated && !isVoiceTutorPage && agentSessionId !== null // Phase 3: Connect only when activated
  });

  useEffect(() => {
    const unsubscribeSummary = voiceAssistantEventBus.on(
      VOICE_EVENTS.SEND_OCR_SUMMARY,
      (summary) => {
        if (summary) {
          sendOcrSummary(summary);
        }
      }
    );

    const unsubscribeValidation = voiceAssistantEventBus.on(
      VOICE_EVENTS.SEND_VALIDATION_STATUS,
      (status) => {
        if (status) {
          sendValidationStatus(status);
        }
      }
    );

    return () => {
      if (typeof unsubscribeSummary === 'function') unsubscribeSummary();
      if (typeof unsubscribeValidation === 'function') unsubscribeValidation();
    };
  }, [sendOcrSummary, sendValidationStatus]);

  useEffect(() => {
    const unsubscribeRedirect = voiceAssistantEventBus.on(
      VOICE_EVENTS.REDIRECT_TO_HOMEWORK_UPLOAD,
      () => {
        navigate('/homework?action=upload');
        setIsExpanded(true);
      }
    );

    return () => {
      if (typeof unsubscribeRedirect === 'function') unsubscribeRedirect();
    };
  }, [navigate]);

  useEffect(() => {
    const unsubscribeReconnect = voiceAssistantEventBus.on(
      VOICE_EVENTS.ENABLE_VOICE_CONNECTION,
      () => {
        if (!isConnected) {
          reconnect();
        }
      }
    );

    return () => {
      if (typeof unsubscribeReconnect === 'function') unsubscribeReconnect();
    };
  }, [isConnected, reconnect]);

  // Auto-start listening after connecting when activated
  useEffect(() => {
    if (isActivated && isConnected && !isListening && !isAgentSpeaking) {
      console.log('🎤 Auto-starting listening after connection');
      startListening();
    }
  }, [isActivated, isConnected, isListening, isAgentSpeaking, startListening]);

  const isProcessingSpeech = isAgentThinking && !isAgentSpeaking;
  const isAgentResponding = isAgentSpeaking;

  // Drag handlers
  const handleMouseDown = (e) => {
    // Don't start drag if clicking the button itself
    if (e.target.closest('button')) return;
    
    // Phase 6: Stop animation when dragging starts
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    setAnimation(null);
    setAnimationClass('');
    
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - currentPositionRef.current.x,
      y: e.clientY - currentPositionRef.current.y
    };
  };

  const handleTouchStart = (e) => {
    // Don't start drag if touching the button itself
    if (e.target.closest('button')) return;
    
    e.preventDefault(); // Prevent scrolling while dragging
    
    // Phase 6: Stop animation when dragging starts
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    setAnimation(null);
    setAnimationClass('');
    
    setIsDragging(true);
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - currentPositionRef.current.x,
      y: touch.clientY - currentPositionRef.current.y
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    
    setPosition({ x: newX, y: newY });
    currentPositionRef.current = { x: newX, y: newY };
  }, [isDragging]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault(); // Prevent scrolling while dragging
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.current.x;
    const newY = touch.clientY - dragStart.current.y;
    
    setPosition({ x: newX, y: newY });
    currentPositionRef.current = { x: newX, y: newY };
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Save current position to localStorage
      localStorage.setItem('floating-voice-position', JSON.stringify(currentPositionRef.current));
    }
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleDragEnd]);

  const handleMicToggle = () => {
    console.log('🎤 Voice assistant clicked', { isActivated, isConnected, isListening });

    if (!isActivated) {
      console.log('🔄 Activating voice assistant...');
      setIsActivated(true);
      setIsExpanded(true);
      return;
    }

    if (!isConnected) {
      console.log('🔄 Reconnecting to voice agent...');
      reconnect();
      startListening();
      setIsExpanded(true);
      return;
    }

    if (isListening) {
      console.log('🛑 Stopping listening and disconnecting');
      stopListening();
      disconnect({ disableRetry: true });
      setIsActivated(false);
    } else {
      console.log('🎤 Starting listening');
      startListening();
      setIsExpanded(true);
    }
  };

  const getMicButtonClass = () => {
    // Phase 4: Show different states including wake word listening
    if (isListening) return 'floating-voice-mic listening';
    if (isAgentResponding) return 'floating-voice-mic speaking';
    if (isConnected) return 'floating-voice-mic ready'; // Blue when connected
    return 'floating-voice-mic idle'; // Gray when idle
  };

  const getStatusText = () => {
    // Phase 3: Show activation and connection status
    if (isAgentResponding) return 'Dino is speaking...';
    if (isProcessingSpeech) return 'Processing...';
    if (isListening) return 'Listening...';
    if (isConnected) return 'Tap to speak with Dino';
    if (isActivated && !isConnected) return 'Connecting...';
    return agentType === 'general' 
      ? 'Click for help navigating the app' 
      : 'Click to ask Dino for help';
  };

  return (
    <div
      ref={iconRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={`floating-voice-container ${animationClass}`}
      style={{
        transform: isDragging || !animation ? 
          `translate(${position.x}px, ${position.y}px)` : 
          undefined, // Let CSS handle transform when animating
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'transform 0.2s ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        WebkitTouchCallout: 'none',
        '--position-x': `${position.x}px`,
        '--position-y': `${position.y}px`
      }}
    >
      {/* Floating Microphone Button - Always visible, draggable */}
      <button
        onClick={handleMicToggle}
        className={getMicButtonClass()}
        disabled={isAgentResponding}
        title={getStatusText()}
        style={{ pointerEvents: 'auto' }} // Ensure button is clickable even when dragging
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="mic-icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Expanded Status Panel (Phase 1: Shows activation message) */}
      {isExpanded && (
        <div className="floating-voice-panel">
          <div className="floating-voice-header">
            <h4>Voice Assistant</h4>
            <button
              onClick={() => setIsExpanded(false)}
              className="close-btn"
            >
              ×
            </button>
          </div>
          
          <div className="floating-voice-status">
            <p className="status-text">{getStatusText()}</p>
            {agentError && (
              <p className="error-text">Error: {agentError}</p>
            )}
            {!isActivated && (
              <p className="info-text" style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', textAlign: 'center' }}>
                {agentType === 'general' 
                  ? "I can help you navigate the app and discover features!" 
                  : "I can help you with your work - just click to start!"}
              </p>
            )}
          </div>

          {(transcript || partialTranscript) && (
            <div className="floating-voice-transcript">
              <p className="transcript-label">You said:</p>
              <p className="transcript-text">
                {transcript}
                {partialTranscript && (
                  <span className="partial">{partialTranscript}</span>
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Phase 1 Wrapper: Always visible on all pages
 * - No longer conditional on sessionId
 * - Only hidden on voice-tutor pages (they have their own mic button)
 */
const FloatingVoiceAssistantComponent = ({ 
  sessionId = null, 
  sessionType = 'homework', 
  pageContext = 'unknown', 
  shouldConnect = false 
}) => {
  // Phase 1: Only hide on voice tutor pages (they have dedicated mic buttons)
  const isVoiceTutorPage = pageContext === 'voice-tutor' || pageContext === 'exercise-voice-tutor';
  
  if (isVoiceTutorPage) {
    console.log('🎤 Hiding FloatingVoiceAssistant on voice tutor page');
    return null;
  }
  
  // Phase 1: Always render, even without sessionId
  return (
    <FloatingVoiceAssistantCore
      sessionId={sessionId}
      sessionType={sessionType}
      pageContext={pageContext}
      shouldConnect={shouldConnect}
    />
  );
};

const FloatingVoiceAssistant = memo(FloatingVoiceAssistantComponent);

export default FloatingVoiceAssistant;
